import React, { useState } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  Edit, 
  Check, 
  Clock, 
  Calendar,
  Flag,
  Folder,
  FolderOpen,
  Hash,
  Archive,
  Trash2
} from 'lucide-react';
import { Goal, Matter, Project } from '../../lib/db';

interface MobileGoalCardProps {
  goal: Goal;
  matters?: Matter[];
  projects?: Project[];
  isCompleted: boolean;
  isExpanded: boolean;
  totalTime: number;
  onToggle: () => void;
  onEdit: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onMarkComplete?: () => void;
  onMarkIncomplete?: () => void;
  renderTaskList: () => React.ReactNode;
  isArchiveView?: boolean;
}

function MobileGoalCard({
  goal,
  matters,
  projects,
  isCompleted,
  isExpanded,
  totalTime,
  onToggle,
  onEdit,
  onArchive,
  onDelete,
  onMarkComplete,
  onMarkIncomplete,
  renderTaskList,
  isArchiveView = false
}: MobileGoalCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  const formatTime = (totalSeconds: number): string => {
    if (totalSeconds === 0) return '';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours === 0 && minutes === 0) return '< 1m';
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return 'bg-red-100 text-red-700';
      case 2: return 'bg-orange-100 text-orange-700';
      case 3: return 'bg-yellow-100 text-yellow-700';
      case 4: return 'bg-green-100 text-green-700';
      case 5: return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const matter = goal.matter_id && matters?.find(m => m.id === goal.matter_id);
  const project = goal.project_id && projects?.find(p => p.id === goal.project_id);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Main Card Content */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <button
                onClick={onToggle}
                className="p-1 -ml-1 rounded hover:bg-gray-100 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <h3 className="text-base font-medium text-gray-900 truncate flex-1">
                {goal.title}
              </h3>
              {(isCompleted || goal.is_complete) && (
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
              )}
            </div>

            {/* Quick Info Row */}
            <div className="mt-2 flex items-center space-x-3 text-sm">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${getPriorityColor(goal.priority)}`}>
                <Flag className="w-3 h-3 mr-1" />
                P{goal.priority}
              </span>
              
              {totalTime > 0 && (
                <span className="inline-flex items-center text-blue-600">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatTime(totalTime)}
                </span>
              )}

              {(goal.start_date || goal.deadline) && (
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="inline-flex items-center text-gray-500 hover:text-gray-700"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  {goal.deadline ? formatDate(goal.deadline) : 'Dates'}
                </button>
              )}
            </div>

            {/* Category & Hierarchy */}
            <div className="mt-2 flex flex-wrap gap-2">
              {goal.category && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                  <Hash className="w-3 h-3 mr-1" />
                  {goal.category}
                </span>
              )}
              
              {project && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                  <FolderOpen className="w-3 h-3 mr-1" />
                  {project.title}
                </span>
              )}
              
              {matter && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
                  <Folder className="w-3 h-3 mr-1" />
                  {matter.title}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Edit className="w-4 h-4 text-gray-400" />
            </button>
            {isCompleted && !goal.is_complete && onMarkComplete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkComplete();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Mark Deliverable Complete"
              >
                <Check className="w-4 h-4 text-gray-400 hover:text-green-600" />
              </button>
            )}
            {goal.is_complete && onMarkIncomplete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkIncomplete();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Mark Deliverable Incomplete"
              >
                <Check className="w-4 h-4 text-green-600" />
              </button>
            )}
            {onArchive && (isArchiveView || isCompleted) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={isArchiveView ? "Restore Deliverable" : "Archive Deliverable"}
              >
                <Archive className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {onDelete && isArchiveView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Delete Deliverable Permanently"
              >
                <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
              </button>
            )}
          </div>
        </div>

        {/* Expandable Details */}
        {showDetails && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-sm">
            {goal.start_date && (
              <div className="flex items-center text-gray-600">
                <span className="font-medium mr-2">Start:</span>
                {formatDate(goal.start_date)}
              </div>
            )}
            {goal.deadline && (
              <div className="flex items-center text-gray-600">
                <span className="font-medium mr-2">Due:</span>
                <span className={goal.deadline < new Date() ? 'text-red-600' : ''}>
                  {formatDate(goal.deadline)}
                </span>
              </div>
            )}
            {goal.description && (
              <div className="text-gray-600">
                <span className="font-medium">Description:</span>
                <p className="mt-1">{goal.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tasks Section */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          {renderTaskList()}
        </div>
      )}
    </div>
  );
}

export default MobileGoalCard;